import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';

import { overviewPanelStyles as styles } from './OverviewPanel.styles';
import type { MetricTooltip } from './overviewTooltips';

/**
 * Building blocks shared by the desktop `OverviewPanel` and the mobile
 * bottom sheet's "Impact Data"/"Selection" tabs, so both surfaces render
 * metric rows and area stats identically.
 */

export interface MetricRowProps {
  label: string;
  value: string;
  /** Small caption shown below the row — e.g. explaining a suppressed "NA". */
  note?: string;
  tooltip?: MetricTooltip;
  emphasize?: boolean;
}

/** Hint + description + source, rendered inside the MUI Tooltip bubble. */
function TooltipBody({ tooltip, compact }: { tooltip: MetricTooltip; compact?: boolean }) {
  if (compact) {
    return (
      <Box sx={styles.tooltipBodyCompact}>
        <Typography sx={styles.tooltipDescriptionCompact}>{tooltip.description}</Typography>
      </Box>
    );
  }
  return (
    <Box sx={styles.tooltipBody}>
      {tooltip.hint && <Typography sx={styles.tooltipHint}>{tooltip.hint}</Typography>}
      <Typography sx={styles.tooltipDescription}>{tooltip.description}</Typography>
      {tooltip.source && (
        <Typography sx={styles.tooltipSource}>Source: {tooltip.source}</Typography>
      )}
    </Box>
  );
}

export function InfoTooltip({
  tooltip,
  compact,
}: {
  tooltip: MetricTooltip;
  /** Single centered paragraph, no hint/source — see `tooltipDescriptionCompact`. */
  compact?: boolean;
}) {
  // Click/tap-to-toggle instead of MUI's default hover/press-and-hold
  // triggers: on mobile, "hover" doesn't exist and the built-in touch
  // trigger needs a ~700ms press-and-hold, which reads as "tooltips don't
  // work" when a user just taps the icon once. All of Tooltip's own open/
  // close triggers (hover, focus, touch) are disabled so nothing but this
  // click handler and the outside-tap listener below can change `open` —
  // a hover-mouseleave or focus-blur firing after a synthetic tap would
  // otherwise immediately re-close what the tap just opened.
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: PointerEvent) => {
      if (anchorRef.current && !anchorRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [open]);

  return (
    <Tooltip
      title={<TooltipBody tooltip={tooltip} compact={compact} />}
      arrow
      placement="top"
      open={open}
      disableTouchListener
      disableFocusListener
      disableHoverListener
      slotProps={{
        tooltip: { sx: styles.tooltipPopup },
        arrow: { sx: styles.tooltipArrow },
      }}
    >
      <InfoOutlinedIcon
        ref={anchorRef}
        sx={styles.infoIcon}
        tabIndex={0}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((o) => !o);
        }}
      />
    </Tooltip>
  );
}

export function MetricRow({ label, value, note, tooltip, emphasize }: MetricRowProps) {
  return (
    <Box sx={styles.metricRow}>
      <Box sx={styles.metricRowTop}>
        <Box sx={styles.metricLabel}>
          <Typography component="span" sx={styles.metricLabelText}>
            {label}
          </Typography>
          {tooltip && <InfoTooltip tooltip={tooltip} />}
        </Box>
        <Typography
          component="span"
          sx={emphasize ? styles.metricValueEmphasis : styles.metricValue}
        >
          {value}
        </Typography>
      </Box>
      {note && <Typography sx={styles.metricNote}>{note}</Typography>}
    </Box>
  );
}

export function AreaStat({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  return (
    <Box>
      <Typography sx={styles.areaStatLabel}>{label}</Typography>
      <Typography sx={styles.areaStatValue}>{value}</Typography>
      {note && <Typography sx={styles.areaStatNote}>{note}</Typography>}
    </Box>
  );
}
